/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { object, string } from 'prop-types';
import { makeStyles } from '@material-ui/core';
import mbpLogger from '@module-logger/mbp';
import bbSignal from '../../../../apis/webchat-apis/v2/bumblebeeSignal';

const useStyles = makeStyles((theme) => ({
    'experience-rating-container': {
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingTop: 1,
        borderRadius: 5,
        width: '100%',
        margin: '8px 2px auto',
        textAlign: 'center',
        border: 'none',
    },
    'rating-prompt': {
        fontFamily: 'area-variable, sans-serif',
        fontStyle: 'normal',
        fontVariationSettings: "'INKT' 0, 'slnt' 0, 'wdth' 100, 'wght' 100",
        fontSize: 13.5,
        fontWeight: 'bold',
        color: '#333',
    },
    'rating-secondary': {
        alignItems: 'center',
        padding: 7,
        borderRadius: 5,
    },
    'rating-prompt-secondary': {
        fontFamily: 'area-variable, sans-serif',
        fontStyle: 'normal',
        fontVariationSettings: "'INKT' 0, 'slnt' 0, 'wdth' 100, 'wght' 100",
        fontSize: 13,
        fontWeight: 700,
        color: '#333',
        flex: 1,
        marginLeft: 10,
        textAlign: 'center',
    },
    'rating-scale': {
        display: 'flex',
        justifyContent: 'center',
        marginTop: '12px',
    },
    'rating-star-wrapper': {
        position: 'relative',
    },
    'rating-star': {
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        opacity: 0.6,
        transition: 'opacity 0.6s ease',
        fontSize: 24,
        color: '#3a3a3a',
        fontWeight: 'bold',
        width: 40,
        height: 40,
    },
    'rating-star.active': {
        opacity: 1,
        outline: 'none',
    },
    'tooltip-experience': {
        position: 'absolute',
        top: '-40%',
        left: '50%',
        transform: 'translateX(-50%)',
        backgroundColor: '#333',
        color: '#fff',
        padding: '1px 2px',
        borderRadius: 4,
        fontSize: 12,
        whiteSpace: 'nowrap',
        opacity: 1,
        transition: 'opacity 0.2s ease',
        zIndex: 5,
        fontFamily: 'area-variable, sans-serif',
        fontStyle: 'normal',
        fontVariationSettings: "'INKT' 0, 'slnt' 0, 'wdth' 100, 'wght' 100",
        fontWeight: 'bold',
    },
    'survey-button': {
        display: 'block',
        margin: '16px auto 0 auto',
        padding: '10px 15px',
        backgroundColor: `${theme.palette.cms?.primary || theme.palette.colorPrimary}`,
        color: 'white',
        fontSize: 16,
        fontWeight: 400,
        textAlign: 'center',
        textDecoration: 'none',
        cursor: 'pointer',
        borderRadius: 5,
        width: 'fit-content',
        fontFamily: 'area-variable, sans-serif',
        fontStyle: 'normal',
        fontVariationSettings: "'INKT' 0, 'slnt' 0, 'wdth' 100, 'wght' 100",
    },
}));
function getSafeSessionJson(key, nestedKey, convId) {
    try {
        if (typeof sessionStorage === 'undefined') return null;

        const storedValue = sessionStorage.getItem(key);
        if (!storedValue) return null;

        const outerObject = JSON.parse(storedValue);
        const nestedJsonString = outerObject?.[nestedKey];

        if (typeof nestedJsonString !== 'string') return null;

        return JSON.parse(nestedJsonString);
    } catch (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            errorMessage: error,
            message: `WebChat: Send Bumblebee Signal error - convid: ${convId} - ERROR: ${error}`,
            convId,
        });
        return null;
    }
}

const ExperienceRating = ({ richData, convId, brandCode }) => {
    const [showExperiencePrompt, setShowExperiencePrompt] = useState(true);
    const [hoveredRating, setHoveredRating] = useState(0);
    const [clickedScore, setClickedScore] = useState(0);
    const auth = getSafeSessionJson('persist:member', 'auth', convId);
    const profile = getSafeSessionJson('persist:member', 'profile', convId);
    const enterprise = getSafeSessionJson('persist:member', 'enterpriseId', convId);
    const browseruuid = localStorage?.getItem('browserUUID') || '';

    const classes = useStyles();
    const {
        'experience-rating-container': experienceRatingContainer,
        'rating-prompt': ratingPrompt,
        'rating-secondary': ratingSecondary,
        'rating-prompt-secondary': ratingPromptSecondary,
        'rating-scale': ratingScale,
        'rating-star-wrapper': ratingStarWrapper,
        'rating-star': ratingStar,
        'rating-star.active': ratingStarActive,
        'tooltip-experience': tooltipExperience,
        'survey-button': surveyButton,
    } = classes;
    const surveyMessages = [
        'We\'re sorry to hear that. Can you tell us what went wrong?',
        'Glad to hear that! Want to tell us what went well?',
    ];
    const scoreMessage = clickedScore;

    const submitExperienceRating = async (score) => {
        setShowExperiencePrompt(false);
        setClickedScore(score);
        try {
            await bbSignal(auth.accessToken,
                {
                    conversation_id: convId,
                    brand_code: brandCode,
                    channel: 'webchat',
                    ga_event_name: 'chat_interaction',
                    interaction_type: 'click',
                    type: 'csat',
                    params: {
                        csat_score: score,
                        customer: {
                            email: profile?.email ?? '',
                            contact_id: profile?.contactId ?? '',
                            browser_uuid: browseruuid ?? '',
                            first: profile?.firstName ?? '',
                            last: profile?.lastName ?? '',
                            phone_number: '',
                            apple_id: '',
                        },
                    },
                    enterprise_id: enterprise?.enterpriseId ?? null,
                },
            );
        } catch (error) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                errorMessage: error,
                message: `WebChat: Send Bumblebee Signal error - convid: ${convId} - ERROR: ${error}`,
                convId,
            });
        }
    };
    const getTooltipMessage = (score) => {
        switch (score) {
            case 1: return 'Not satisfied';
            case 2: return 'Slightly satisfied';
            case 3: return 'Neutral';
            case 4: return 'Satisfied';
            case 5: return 'Extremely satisfied';
            default: return '';
        }
    };

    const StarIconFilled = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="#FFD700"
            aria-hidden="true"
        >
            <path d="M12 .587l3.668 7.568L24 9.748l-6 5.848L19.335 24 12 20.201 4.665 24 6 15.596 0 9.748l8.332-1.593z" />
        </svg>
    );

    const StarIconOutlined = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="rgba(255, 215, 0, 0.3)"
            stroke="#FFD700"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
    );

    return (

        <div className={experienceRatingContainer}>
            <div>
                {showExperiencePrompt ? (
                    <>
                        <p className={ratingPrompt}>How would you rate your chat experience today?</p>
                        <div className={ratingScale}>
                            {[1, 2, 3, 4, 5].map((score) => (
                                <div className={ratingStarWrapper} key={score}>
                                    <button
                                        className={hoveredRating >= score ? `${ratingStar} ${ratingStarActive}` : ratingStar}
                                        onClick={() => submitExperienceRating(score)}
                                        onMouseEnter={() => setHoveredRating(score)}
                                        onMouseLeave={() => setHoveredRating(0)}
                                        onFocus={() => setHoveredRating(score)}
                                        onBlur={() => setHoveredRating(0)}
                                        aria-label={`Rating ${score}`}
                                        type="button"
                                    >
                                        <span className="rating-label">
                                            {hoveredRating >= score ? <StarIconFilled /> : <StarIconOutlined />}
                                        </span>
                                    </button>
                                    {hoveredRating === score && (
                                        <div className={tooltipExperience}>
                                            {getTooltipMessage(score)}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className={ratingSecondary} style={{ pointerEvents: richData.clickable === 'N' ? 'none' : 'all' }}>
                        <p className={ratingPromptSecondary}>
                            {scoreMessage < 4 ? surveyMessages[0] : surveyMessages[1]}
                        </p>
                        <a className={surveyButton} href={richData.url} target="_blank" rel="noreferrer">
                            Share More Feedback
                        </a>
                    </div>
                )}
            </div>
        </div>

    );
};
ExperienceRating.propTypes = {
    convId: string.isRequired,
    richData: object.isRequired,
    brandCode: string.isRequired,
};

export default ExperienceRating;
